import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import BaseTableReport from 'components/atoms/base-table/report/report';
import BaseTag from 'components/atoms/base-tag';
import { Routes } from 'router/routes';
import { ADMIN_MEDICATIONS_PER_PAGE } from 'infrastructure/hooks/medications';

import s from './styles.module.scss';

import type { TableProps } from 'antd';
import type {
  IAdminMedicationListItem,
  IAdminMedicationListResponse,
} from 'infrastructure/interfaces';
import type { TMedicationsFilterState } from '../medications-filter/use-medications-filter';

interface AdminMedicationsTableProps {
  filter: TMedicationsFilterState;
  updateFilter: (patch: Partial<TMedicationsFilterState>) => void;
  data?: IAdminMedicationListResponse;
  isFetching: boolean;
}

export const AVAILABLE_TAG_COLOR = '#35b37e';
export const UNAVAILABLE_TAG_COLOR = '#a0aec0';

const AdminMedicationsTable: React.FC<AdminMedicationsTableProps> = ({
  filter,
  updateFilter,
  data,
  isFetching,
}) => {
  const { t } = useTranslation();

  const handleTableSort = useCallback(
    (sorter: any) => {
      const [sortField, sortDirection] = sorter;

      if (sortField && sortDirection) {
        updateFilter({
          sort: JSON.stringify([sortField, sortDirection]),
          page: 1,
        });
      } else {
        updateFilter({ sort: undefined, page: 1 });
      }
    },
    [updateFilter],
  );

  const currentSort = useMemo<{
    field: string | null;
    order: 'ascend' | 'descend' | null;
  }>(() => {
    if (!filter.sort) return { field: null, order: null };
    try {
      const [field, direction] = JSON.parse(filter.sort);
      return {
        field,
        order: direction === 'asc' ? 'ascend' : 'descend',
      };
    } catch {
      return { field: null, order: null };
    }
  }, [filter.sort]);

  const columns: TableProps<IAdminMedicationListItem>['columns'] = useMemo(
    () => [
      {
        title: t('labels.medicationName'),
        key: 'name',
        dataIndex: 'name',
        width: '40%',
        sorter: true,
        sortOrder: currentSort.field === 'name' ? currentSort.order : null,
        render: (_, record) => (
          <Link
            to={Routes.adminMedication(record.guid)}
            className={s['name-button']}
          >
            {record?.name || '—'}
          </Link>
        ),
      },
      {
        title: t('labels.manufacturer'),
        dataIndex: 'manufacturer',
        key: 'manufacturer',
        width: '15%',
        sorter: true,
        sortOrder:
          currentSort.field === 'manufacturer' ? currentSort.order : null,
        render: (value?: string) => value || '-',
      },
      {
        title: t('labels.couponDescription'),
        dataIndex: 'couponDescription',
        key: 'couponDescription',
        width: '25%',
        sorter: true,
        sortOrder:
          currentSort.field === 'couponDescription' ? currentSort.order : null,
        render: (value?: string) => {
          if (!value) return '-';
          return (
            <Tooltip title={value} placement="bottomLeft">
              <span className={s.coupon}>{value}</span>
            </Tooltip>
          );
        },
      },
      {
        title: t('labels.otc'),
        dataIndex: 'otc',
        key: 'otc',
        width: '10%',
        sorter: true,
        sortOrder: currentSort.field === 'otc' ? currentSort.order : null,
        render: (value: boolean) => (value ? t('labels.yes') : t('labels.no')),
      },
      {
        title: t('labels.papAvailable'),
        dataIndex: 'papAvailable',
        key: 'papAvailable',
        width: '10%',
        sorter: true,
        sortOrder:
          currentSort.field === 'papAvailable' ? currentSort.order : null,
        render: (value: boolean) => (
          <BaseTag
            label={value ? t('labels.available') : t('labels.notAvailable')}
            color={value ? AVAILABLE_TAG_COLOR : UNAVAILABLE_TAG_COLOR}
          />
        ),
      },
    ],
    [t, currentSort],
  );

  const pagination = {
    current: filter.page,
    pageSize: ADMIN_MEDICATIONS_PER_PAGE,
    total: data?.count ?? 0,
    onChange: (page: number) => updateFilter({ page }),
  };

  return (
    <BaseTableReport
      className={s.table}
      columns={columns}
      dataSource={data?.items ?? []}
      rowKey="guid"
      isLoading={isFetching}
      pagination={pagination}
      paginationAlign="right"
      onSort={handleTableSort}
      headerTitle={t('labels.medications')}
      showCount
      totalCount={data?.count}
      dataCy="admin-medications-table"
      anchor
      displayTopPagination={false}
      hidePaginationOnSinglePage={false}
    />
  );
};

export default AdminMedicationsTable;
